import React, { useEffect, useState, useRef } from "react";
import { Spinner, Table } from "react-bootstrap";
import "./participantfiles.css";
import { useAuth } from "../../../firebase/AuthProvider";
import { TeamUser } from "../../../interfaces/team/TeamUser";
import { getUserInfoByUserId } from "../../../services/userServices/UserInfoService";
import UserInfo from "../../../interfaces/UserInfo";
import { getDriveData } from "../../../services/fileServices/FileService";
import { mapParticipantsToDriveItems } from "../../../utilities/MapFunctions";
import {
  DriveItem,
  Participant,
} from "../../../interfaces/caseFileDrive/DriveFiles";
import { useNavigate, useParams } from "react-router-dom";
import { getTeamBySupervisor } from "../../../services/userServices/TeamService";
import { formatFileSize } from "../../../utilities/fileUtils";
import { convertUtcToLocalDate } from "../../../utilities/dateUtils";
import backarrowbtn from "../../../assets/images/backarrowbtn.svg";
import AvatarImage from "../../ui/Avatar/AvatarImage";
import { FileText, FolderOpen, User2, Users } from "lucide-react";
import { useDriveContext } from "../../../contexts/DriveContext";
import NoParticipantsFound from "./noParticipantsFound";
import "../../../assets/css/drive.css";

const Participantfiles = () => {
  const { currentUser, currentRoles } = useAuth();
  const navigate = useNavigate();
  const { searchTerm, pagination, setPagination, setTotalItems } = useDriveContext();
  const carouselRef = useRef<HTMLDivElement>(null);
  const [employees, setEmployees] = useState<TeamUser[]>();
  const [userInfo, setUserInfo] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [participants, setParticipants] = useState<Participant[] | null>(null);
  const [items, setItems] = useState<DriveItem[]>([]);
  const { employeeId } = useParams<{ employeeId?: string }>();
  const [employeeName, setEmployeeName] = useState<string>("");
  const [sortKey, setSortKey] = useState<"name" | "updateDate" | "size" | null>(
    null
  );
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [showLeftArrow, setShowLeftArrow] = useState<boolean>(false);
  const [showRightArrow, setShowRightArrow] = useState<boolean>(true);

  const hasHigherRoleThanSC = currentRoles?.some(
    (role) =>
      role.name === "SUPERVISOR" ||
      role.name === "OWNER" ||
      role.name === "ADMIN" ||
      role.name === "PROGRAM MANAGER"
  );

  // Function to abbreviate role names
  const getRoleAbbreviation = (role: string | undefined | null): string => {
    if (!role) return '';
    const roleUpper = role.toUpperCase().trim();
    if (roleUpper === 'PROGRAM MANAGER') return 'PM';
    if (roleUpper === 'SUPERVISOR') return 'SUP';
    return role; // Return original if no abbreviation found
  };

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        if (!currentUser) return;
        const authToken = await currentUser.getIdToken();

        // Fetch employees if supervisor (without fetching user info in loop)
        if (hasHigherRoleThanSC && !employeeId) {
          const employeesData = await getTeamBySupervisor(authToken);
          if (employeesData) {
            setEmployees(employeesData);
            // Don't fetch userInfo in loop - only fetch when carousel card is clicked
          }
        }

        // Always fetch drive data for the current user or the selected employee
        let data;
        try {
          // Add try-catch around getDriveData
          if (employeeId && currentRoles && hasHigherRoleThanSC) {
            data = await getDriveData(authToken, employeeId);
          } else {
            data = await getDriveData(authToken);
          }
          // Ensure data is an array before processing
          const actualData = data || []; // If data is null/undefined, treat as empty array

          setParticipants(actualData);
          const driveItems = mapParticipantsToDriveItems(actualData);
          setItems(driveItems);
        } catch (driveError) {
          console.error("Failed to fetch drive data:", driveError);
          // Explicitly clear participants and items on error
          setParticipants([]);
          setItems([]);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [currentUser, currentRoles, employeeId]);

  // Reset pagination when search term changes
  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageNumber: 1,
    }));
  }, [searchTerm, setPagination]);

  // Check scroll position on mount and when employees changes
  useEffect(() => {
    if (hasHigherRoleThanSC && !employeeId && employees && employees.length > 0) {
      setTimeout(() => {
        if (carouselRef.current) {
          const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
          setShowLeftArrow(scrollLeft > 0);
          setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
        }
      }, 100);
    }
  }, [employees, hasHigherRoleThanSC, employeeId]);

  // Update total items in context for common pagination
  // Calculate totalItems inside useEffect to avoid conditional hook call
  useEffect(() => {
    if (!loading && items.length >= 0) {
      const filteredItems = items.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );

      // Sorting function
      const sortedItems = [...filteredItems].sort((a, b) => {
        if (!sortKey) return 0;
        let aValue =
          sortKey === "name"
            ? a.name.toLowerCase()
            : a.updateDate
              ? (() => {
                const localDate = convertUtcToLocalDate(a.updateDate);
                return localDate ? localDate.getTime() : 0;
              })()
              : 0;
        let bValue =
          sortKey === "name"
            ? b.name.toLowerCase()
            : b.updateDate
              ? (() => {
                const localDate = convertUtcToLocalDate(b.updateDate);
                return localDate ? localDate.getTime() : 0;
              })()
              : 0;
        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });

      const totalItems = sortedItems.length;
      setTotalItems(totalItems);
    } else {
      setTotalItems(0);
    }
  }, [loading, items, searchTerm, sortKey, sortDirection, setTotalItems]);

  if (loading)
    return (
      <div className="text-center p-5">
        {" "}
        <Spinner size="sm" /> Loading...
      </div>
    );

  const filteredItems = items.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sorting function
  const sortedItems = [...filteredItems].sort((a, b) => {
    if (!sortKey) return 0;
    let aValue: any;
    let bValue: any;

    switch (sortKey) {
      case "name":
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case "updateDate":
        aValue = a.updateDate
          ? (() => {
            const localDate = convertUtcToLocalDate(a.updateDate);
            return localDate ? localDate.getTime() : 0;
          })()
          : 0;
        bValue = b.updateDate
          ? (() => {
            const localDate = convertUtcToLocalDate(b.updateDate);
            return localDate ? localDate.getTime() : 0;
          })()
          : 0;
        break;
      case "size":
        aValue = a.size || 0;
        bValue = b.size || 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  // Pagination logic
  const totalItems = sortedItems.length;
  const startIndex = (pagination.pageNumber - 1) * pagination.pageSize;
  const endIndex = startIndex + pagination.pageSize;
  const paginatedItems = sortedItems.slice(startIndex, endIndex);

  const handleSort = (key: "name" | "updateDate" | "size") => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (key: "name" | "updateDate" | "size") => {
    if (sortKey !== key) return "⇅";
    return sortDirection === "asc" ? "▲" : "▼";
  };

  // Get highest role for a user
  const getHighestRole = (roles: any[] | undefined) => {
    if (!Array.isArray(roles) || roles.length === 0) {
      return null;
    }

    const rolePriority = [
      "ADMIN",
      "OWNER",
      "PROGRAM MANAGER",
      "SUPERVISOR",
      "SC",
      "ASSESSOR",
    ];

    const roleLabelMap: any = {
      ADMIN: "Admin",
      OWNER: "Owner",
      SUPERVISOR: "Supervisor",
      "PROGRAM MANAGER": "Program manager",
      SC: "Support coordinator",
      ASSESSOR: "Assessor",
    };

    const normalizedRoles = roles
      .filter(Boolean)
      .map((role) => (typeof role === "string" ? role : role.name || "").toUpperCase());

    // Check for highest priority role
    const matchedRole = rolePriority.find((role) =>
      normalizedRoles.includes(role)
    );

    if (matchedRole) {
      return roleLabelMap[matchedRole];
    }

    // Fallback: role not in priority list
    const fallbackRole = roles[0];
    if (typeof fallbackRole === "string") {
      return fallbackRole
        .toLowerCase()
        .replace(/\b\w/g, (char: string) => char.toUpperCase());
    }
    return fallbackRole?.name || "";
  };

  // Check scroll position to show/hide arrows
  const checkScrollPosition = () => {
    if (!carouselRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
  };

  // Scroll carousel left
  const scrollLeft = () => {
    if (carouselRef.current) {
      const scrollAmount = carouselRef.current.clientWidth * 0.8;
      carouselRef.current.scrollBy({
        left: -scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Scroll carousel right
  const scrollRight = () => {
    if (carouselRef.current) {
      const scrollAmount = carouselRef.current.clientWidth * 0.8;
      carouselRef.current.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Handle carousel card click - fetch user info and drive data for that employee
  const handleCarouselCardClick = async (empId: string) => {
    if (!currentUser) return;

    setLoading(true);
    try {
      const authToken = await currentUser.getIdToken();

      // Fetch user info for the clicked employee
      const userInfoData = await getUserInfoByUserId(empId, authToken);
      if (userInfoData) {
        setUserInfo([userInfoData]);
      }

      // Fetch drive data for the selected employee
      const data = await getDriveData(authToken, empId);
      const actualData = data || [];

      setParticipants(actualData);
      const driveItems = mapParticipantsToDriveItems(actualData);
      setItems(driveItems);

      // Navigate to the employee page
      navigate(`employee/${empId}`);
    } catch (error) {
      console.error("Failed to fetch employee data:", error);
      setParticipants([]);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Called when user clicks a folder/file row or icon
  const handleItemClick = (item: DriveItem) => {
    // Allow all roles (including SC) to navigate to participant profile
    navigate(`/participant/${item.id}/participant-snapshot`);
  };

  return (
    <>
      {/* Section for My Team Carousel (if supervisor and no employeeId) */}
      {hasHigherRoleThanSC && !employeeId && employees && employees.length > 0 && (
        <div className="common_content_inner mby_css">
          <div className="team_carousel_wrapper">
            {showLeftArrow && (
              <button
                className="team_carousel_arrow team_carousel_arrow_left"
                onClick={scrollLeft}
                aria-label="Scroll left"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="7"
                  height="12"
                  viewBox="0 0 7 12"
                  fill="none"
                  style={{ transform: "rotate(180deg)" }}
                >
                  <path
                    d="M0.699375 0L6.35938 5.66L0.699375 11.32L-0.00062561 10.61L4.94938 5.66L-0.00062561 0.71L0.699375 0Z"
                    fill="black"
                  />
                </svg>
              </button>
            )}
            <div
              className="team_carousel_container"
              ref={carouselRef}
              onScroll={checkScrollPosition}
            >
              {employees.map((emp, index) => {
                // Find userInfo for this employee if it's been fetched
                const userInfoForEmp = userInfo.find(u => u.id === emp.id);
                const highestRole = userInfoForEmp ? getHighestRole(userInfoForEmp.roles) : null;
                return (
                  <div
                    key={emp.id || index}
                    className="team_member_card"
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleCarouselCardClick(emp.id) }}
                  >
                    <div className="team_member_content">
                      <div className="team_member_avatar">
                        {
                          emp.avatar ? (
                            <img src={emp.avatar + ""} alt="avatar" />) :
                            (<AvatarImage userId={''} />
                            )
                        }
                        {/* <img src={emp.avatar + ""} alt="avatar" /> */}
                        {/* <AvatarImage userId={emp.id} /> */}
                      </div>
                      <div className="team_member_info">
                        <div className="team_member_name">
                          {emp.firstName} {emp.lastName}
                        </div>
                        {/* <div className="team_member_details">
                        
                        {(emp as any)?.participantCount !== undefined && (emp as any)?.participantCount !== null && (
                     <span className="team_member_count ">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="13" viewBox="0 0 18 13" fill="none">
                     <path d="M12 8.18164C13.2222 8.18166 14.3431 8.4576 15.2617 8.9668L15.4424 9.07129C16.5021 9.71303 17.1998 10.6475 17.4805 11.7764L17.4814 11.7793C17.5042 11.8685 17.5062 11.9617 17.4873 12.0518C17.4685 12.1416 17.4283 12.226 17.3711 12.2998C17.3215 12.361 17.2585 12.4115 17.1855 12.4463C17.1112 12.4817 17.0295 12.5004 16.9463 12.5H7.05371C6.97021 12.5004 6.88803 12.4819 6.81348 12.4463C6.77601 12.4284 6.74084 12.4063 6.70898 12.3809L6.62402 12.2959C6.56836 12.2229 6.53005 12.1392 6.51172 12.0508C6.49309 11.9608 6.49553 11.8674 6.51855 11.7783L6.51953 11.7764C6.80116 10.6626 7.4972 9.7324 8.55664 9.08594C9.50226 8.50924 10.7254 8.18164 12 8.18164ZM4.91309 7.83301C5.74916 7.83304 6.47953 7.96608 7.03809 8.20703C6.01149 9.01316 5.29976 10.0763 4.9707 11.3184H0.961914C0.892031 11.3186 0.822998 11.3032 0.760742 11.2734C0.69893 11.2438 0.645962 11.2005 0.604492 11.1484C0.557914 11.0877 0.526043 11.0178 0.510742 10.9443C0.495313 10.87 0.496717 10.7933 0.515625 10.7197L0.516602 10.7178C0.742362 9.82488 1.29987 9.0786 2.15234 8.55762C2.90865 8.09631 3.88942 7.83301 4.91309 7.83301ZM4.91211 1.68164C5.53295 1.68164 6.07424 1.91367 6.45898 2.32129V2.32227C6.8475 2.7337 7.04547 3.29489 6.99902 3.91797V3.91895C6.9065 5.19507 5.91743 6.14746 4.91211 6.14746C3.90442 6.14726 2.91573 5.19409 2.82324 3.91797C2.78486 3.36509 2.9335 2.86514 3.22852 2.47656L3.36328 2.31738C3.74392 1.91424 4.28916 1.68172 4.91211 1.68164ZM12 0.5C12.7666 0.500026 13.44 0.785594 13.9131 1.28613C14.3296 1.72696 14.5668 2.31312 14.582 2.9707L14.5742 3.25684C14.5195 3.99989 14.2191 4.69004 13.7354 5.20215C13.2375 5.72877 12.6195 5.99997 12 6C11.3808 6 10.7623 5.72804 10.2627 5.20117C9.83851 4.75215 9.5549 4.16786 9.45508 3.53223L9.42383 3.25684L9.41602 2.96777C9.43122 2.30358 9.66885 1.71706 10.0811 1.28125C10.5545 0.780859 11.2247 0.5 12 0.5Z" stroke="#747474"/>
                   </svg>
                    {(emp as any)?.participantCount} </span>
                    )}
                      
                        </div> */}
                        {/* <div className="team_member_details">
                          {highestRole && (
                            <span className="team_member_role">
                              {highestRole}
                            </span>
                          )}
                        </div> */}
                        <div className="roleParticipantCountContainer">
                          <span className="ParticipantCount participantrolecounttext">

                            <svg className="mb-[1px]" xmlns="http://www.w3.org/2000/svg" width="16" height="11" viewBox="0 0 18 13" fill="none">
                              <path d="M12 8.18164C13.2222 8.18166 14.3431 8.4576 15.2617 8.9668L15.4424 9.07129C16.5021 9.71303 17.1998 10.6475 17.4805 11.7764L17.4814 11.7793C17.5042 11.8685 17.5062 11.9617 17.4873 12.0518C17.4685 12.1416 17.4283 12.226 17.3711 12.2998C17.3215 12.361 17.2585 12.4115 17.1855 12.4463C17.1112 12.4817 17.0295 12.5004 16.9463 12.5H7.05371C6.97021 12.5004 6.88803 12.4819 6.81348 12.4463C6.77601 12.4284 6.74084 12.4063 6.70898 12.3809L6.62402 12.2959C6.56836 12.2229 6.53005 12.1392 6.51172 12.0508C6.49309 11.9608 6.49553 11.8674 6.51855 11.7783L6.51953 11.7764C6.80116 10.6626 7.4972 9.7324 8.55664 9.08594C9.50226 8.50924 10.7254 8.18164 12 8.18164ZM4.91309 7.83301C5.74916 7.83304 6.47953 7.96608 7.03809 8.20703C6.01149 9.01316 5.29976 10.0763 4.9707 11.3184H0.961914C0.892031 11.3186 0.822998 11.3032 0.760742 11.2734C0.69893 11.2438 0.645962 11.2005 0.604492 11.1484C0.557914 11.0877 0.526043 11.0178 0.510742 10.9443C0.495313 10.87 0.496717 10.7933 0.515625 10.7197L0.516602 10.7178C0.742362 9.82488 1.29987 9.0786 2.15234 8.55762C2.90865 8.09631 3.88942 7.83301 4.91309 7.83301ZM4.91211 1.68164C5.53295 1.68164 6.07424 1.91367 6.45898 2.32129V2.32227C6.8475 2.7337 7.04547 3.29489 6.99902 3.91797V3.91895C6.9065 5.19507 5.91743 6.14746 4.91211 6.14746C3.90442 6.14726 2.91573 5.19409 2.82324 3.91797C2.78486 3.36509 2.9335 2.86514 3.22852 2.47656L3.36328 2.31738C3.74392 1.91424 4.28916 1.68172 4.91211 1.68164ZM12 0.5C12.7666 0.500026 13.44 0.785594 13.9131 1.28613C14.3296 1.72696 14.5668 2.31312 14.582 2.9707L14.5742 3.25684C14.5195 3.99989 14.2191 4.69004 13.7354 5.20215C13.2375 5.72877 12.6195 5.99997 12 6C11.3808 6 10.7623 5.72804 10.2627 5.20117C9.83851 4.75215 9.5549 4.16786 9.45508 3.53223L9.42383 3.25684L9.41602 2.96777C9.43122 2.30358 9.66885 1.71706 10.0811 1.28125C10.5545 0.780859 11.2247 0.5 12 0.5Z" stroke="#747474" />
                            </svg>
                            {(emp as any)?.participantCount ?? 0}
                          </span>
                          <span className="role participantrolecounttext">
                            <svg className="mb-[1px]" xmlns="http://www.w3.org/2000/svg" width="12" height="11" viewBox="0 0 12 13" fill="none">
                              <path d="M3.5 10.0996C4.58775 8.92722 6.60004 8.87203 7.69979 10.0996M6.76384 6.49974C6.76384 7.16271 6.24126 7.69968 5.5957 7.69968C4.95073 7.69968 4.42755 7.16271 4.42755 6.49974C4.42755 5.83678 4.95073 5.2998 5.5957 5.2998C6.24066 5.2998 6.76444 5.83678 6.76444 6.49974H6.76384Z" stroke="#747474" strokeLinecap="round" />
                              <path d="M4.09982 1.70117C2.5135 1.70717 1.66454 1.76237 1.11497 2.28314C0.5 2.86571 0.5 3.80347 0.5 5.67897V8.52103C0.5 10.3965 0.5 11.3349 1.11497 11.9169C1.72994 12.5 2.71989 12.5 4.69979 12.5H6.4997C8.4796 12.5 9.46955 12.5 10.0845 11.9175C10.6995 11.3343 10.6995 10.3965 10.6995 8.52163V5.67897C10.6995 3.80347 10.6995 2.86571 10.0845 2.28314C9.53495 1.76237 8.68659 1.70717 7.09967 1.70117" stroke="#747474" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M4.2646 1.47915C4.3222 1.23016 4.35099 1.10537 4.40619 1.00397C4.47481 0.883328 4.56818 0.778565 4.68017 0.696566C4.79216 0.614567 4.92022 0.557188 5.05596 0.528199C5.17775 0.5 5.31935 0.5 5.60133 0.5C5.88332 0.5 6.02491 0.5 6.14671 0.528199C6.28244 0.557188 6.41051 0.614567 6.52249 0.696566C6.63448 0.778565 6.72785 0.883328 6.79647 1.00397C6.85167 1.10597 6.88047 1.23016 6.93807 1.47915L6.98786 1.69514C7.08986 2.13612 7.14085 2.35691 7.06406 2.5267C7.01172 2.63939 6.92662 2.73369 6.81987 2.79729C6.65128 2.89988 6.40049 2.89988 5.90012 2.89988H5.30255C4.80217 2.89988 4.55138 2.89988 4.38279 2.79789C4.27595 2.73413 4.19085 2.63961 4.13861 2.5267C4.06181 2.35691 4.11281 2.13672 4.2148 1.69514L4.2646 1.47915Z" stroke="#747474" />
                            </svg>
                            <span className="participantrolecounttext-text">{getRoleAbbreviation((emp as any)?.role)}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* {(emp as any)?.participantCount !== undefined && (emp as any)?.participantCount !== null && (
                      <span className="team_member_participant_count">{(emp as any)?.participantCount}</span>
                    )} */}
                  </div>
                );
              })}
            </div>
            {showRightArrow && (
              <button
                className="team_carousel_arrow team_carousel_arrow_right"
                onClick={scrollRight}
                aria-label="Scroll right"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="7"
                  height="12"
                  viewBox="0 0 7 12"
                  fill="none"
                >
                  <path
                    d="M0.699375 0L6.35938 5.66L0.699375 11.32L-0.00062561 10.61L4.94938 5.66L-0.00062561 0.71L0.699375 0Z"
                    fill="black"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
      {/* Section for Participant Files (always shown unless supervisor is viewing team and no files) */}
      {(employeeId ||
        !hasHigherRoleThanSC ||
        (hasHigherRoleThanSC &&
          !employeeId &&
          participants &&
          participants.length > 0)) && (
          <div className={`common_content_inner mby_css ${pagination.pageSize === 10 ? 'drive-page-size-10' : ''} drive-content-container`}>
            <div className="personal_flx">
              <div className="personal_flx_left">
                <div className="breadcrumb-container !w-full">
                  <div
                    className="drive-breadcrumb-container !w-full"
                    style={{ cursor: "pointer" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="12" viewBox="0 0 14 12" fill="none" style={{ display: "block" }}>
                        <path d="M13.7091 5.11997C13.5676 4.92451 13.3809 4.76608 13.1651 4.65815C12.9492 4.55022 12.7105 4.49597 12.4692 4.50001H12.3092V3.50008C12.3093 3.30056 12.2695 3.10303 12.1922 2.91907C12.115 2.73511 12.0018 2.56841 11.8594 2.42874C11.7169 2.28906 11.548 2.17921 11.3625 2.10562C11.1771 2.03203 10.9788 1.99618 10.7793 2.00017H7.53952C7.4183 1.99946 7.30065 1.95907 7.20454 1.88518L5.20467 0.325279C4.93095 0.114519 4.59519 0.000253803 4.24973 0.000300032H1.5299C1.33042 -0.00369037 1.13215 0.0321584 0.946699 0.105747C0.761246 0.179336 0.592338 0.289185 0.449866 0.428863C0.307394 0.568542 0.194222 0.735241 0.116975 0.919201C0.0397293 1.10316 -3.81466e-05 1.30068 1.76147e-06 1.5002V10.4996C-0.000432564 10.7764 0.0794602 11.0473 0.229987 11.2796C0.368331 11.4995 0.560071 11.6808 0.787358 11.8067C1.01464 11.9326 1.27009 11.9989 1.5299 11.9995H11.0293C11.3557 12.0077 11.6759 11.9091 11.9412 11.7187C12.2065 11.5283 12.4024 11.2565 12.4992 10.9446L13.9291 6.44489C13.9979 6.22214 14.0139 5.98642 13.9757 5.75643C13.9375 5.52645 13.8462 5.30853 13.7091 5.11997ZM0.999938 1.5002C0.999815 1.43195 1.01367 1.36438 1.04065 1.30168C1.06763 1.23898 1.10717 1.18247 1.15682 1.13563C1.20647 1.08879 1.26519 1.05261 1.32935 1.02933C1.39351 1.00604 1.46177 0.996141 1.5299 1.00024H4.24973C4.3726 1.00023 4.49207 1.04064 4.58971 1.11523L6.58958 2.67513C6.8614 2.88585 7.19559 3.00018 7.53952 3.00011H10.7793C10.8475 2.99601 10.9157 3.00591 10.9799 3.0292C11.044 3.05248 11.1028 3.08866 11.1524 3.1355C11.2021 3.18234 11.2416 3.23886 11.2686 3.30156C11.2955 3.36426 11.3094 3.43182 11.3093 3.50008V4.50001H3.30979C2.98588 4.49516 2.66911 4.5953 2.40685 4.78546C2.14459 4.97562 1.95095 5.24557 1.85488 5.55494L0.999938 8.26977V1.5002ZM12.9992 6.14491L11.5693 10.6446C11.5372 10.7509 11.4706 10.8435 11.3801 10.9078C11.2896 10.972 11.1802 11.0043 11.0693 10.9996H1.5699C1.45714 10.9993 1.34718 10.9644 1.25492 10.8996L2.85482 5.85493C2.88691 5.74864 2.95347 5.65607 3.044 5.5918C3.13452 5.52752 3.24387 5.49521 3.35479 5.49995H12.4692C12.5535 5.49936 12.6367 5.5185 12.7122 5.55583C12.7877 5.59316 12.8535 5.64765 12.9042 5.71494C12.9518 5.7746 12.9851 5.84437 13.0016 5.9189C13.018 5.99343 13.0172 6.07074 12.9992 6.14491Z" fill="#7E57C2" />
                      </svg>
                      <span
                        style={{
                          color: "#7E57C2",
                          textAlign: "center",
                          fontFamily: "Poppins",
                          fontSize: "1rem",
                          fontStyle: "normal",
                          fontWeight: 400,
                          lineHeight: "1.25rem",
                          display: "flex",
                          alignItems: "center"
                        }}
                        onClick={() => navigate("/drive/caseFileDrive")}
                      >
                        Drive
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="drive-table-wrapper">
              <Table responsive className="drive-table participant-files-table">
                <thead>
                  <tr>
                    <th
                      className="th-head"
                      onClick={() => handleSort("name")}
                      style={{ cursor: "pointer" }}
                    >
                      Name{" "}
                      <span className="text-[#6A7282]">
                        {getSortIcon("name")}
                      </span>
                    </th>

                    {/* <th
                      className="th-head"
                      onClick={() => handleSort("name")}
                      style={{ cursor: "pointer" }}
                    >
                      Participant{" "}
                      <span className="text-[#6A7282]">
                        {getSortIcon("name")}
                      </span>
                    </th> */}

                    <th
                      className="th-head"
                      onClick={() => handleSort("updateDate")}
                      style={{ cursor: "pointer" }}
                    >
                      Created{" "}
                      <span className="text-[#6A7282]">
                        {getSortIcon("updateDate")}
                      </span>
                    </th>

                    <th
                      className="th-head"
                      onClick={() => handleSort("updateDate")}
                      style={{ cursor: "pointer" }}
                    >
                      Last Updated{" "}
                      <span className="text-[#6A7282]">
                        {getSortIcon("updateDate")}
                      </span>
                    </th>

                    <th
                      className="th-head"
                      onClick={() => handleSort("size")}
                      style={{ cursor: "pointer" }}
                    >
                      File Size{" "}
                      <span className="text-[#6A7282]">
                        {getSortIcon("size")}
                      </span>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {!loading && (!participants || participants.length === 0 || paginatedItems.length === 0) ? (
                    <tr className="no-data-row">
                      <td colSpan={4} style={{ padding: 0, border: "none" }}>
                        <div className="no-participants-found-container">
                          <NoParticipantsFound />
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedItems.map((item, index) => (
                      <tr key={index} onClick={() => handleItemClick(item)}>
                        {/* Folder icon column */}
                        <td>
                          <div className="tableicon-tab">
                            {item.isFolder ? (
                              <span className="tableicon">
                                {" "}
                                <FolderOpen className="tableicon-svg" />
                              </span>
                            ) : (
                              <span className="tableicon">
                                <FileText className="tableicon-svg" />
                              </span>
                            )}
                            <span className=" font-normal text-[#354152] text-lg tracking-[0] leading-6 truncate">
                              {item.name}
                            </span>
                          </div>
                        </td>

                        {/* Name */}
                        {/* <td>{item.name}</td> */}

                        {/* Created date */}
                        <td>
                          {item?.updateDate
                            ? (() => {
                              const localDate = convertUtcToLocalDate(item.updateDate);
                              return localDate ? localDate.toLocaleDateString() : "N/A";
                            })()
                            : "N/A"}
                        </td>

                        {/* Last Updated */}
                        <td>
                          {item?.updateDate
                            ? (() => {
                              const localDate = convertUtcToLocalDate(item.updateDate);
                              return localDate ? localDate.toLocaleDateString() : "N/A";
                            })()
                            : "N/A"}
                        </td>

                        {/* File size */}
                        <td>{formatFileSize(item.size ?? 0)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        )}
    </>
  );
};

export default Participantfiles;
